import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MuiTooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  useDisplayTimeZone,
  useFormatTimeOfDay,
} from '../util/displayTimeZone';
import {
  getFocusOverlay,
  getMyFocusOverlay,
  type FocusOverlay as FocusOverlayData,
  type FocusParticipant,
} from '../api/focusOverlay';
import { reprocessInstance } from '../api/procedureInstances';
import { focusXDomain } from './focusOverlayAxis';
import { TraceZoom, useTraceViewport } from './traceViewport';
import { MediaStrip, type MediaStripHandle } from './MediaStrip';
import { CaptionTrack, type CaptionEntry } from './CaptionTrack';
import { PinnedMomentLabel } from './PinnedMomentLabel';
import { MomentInspector } from './MomentInspector';
import { isSameMoment } from '../util/pinnedMoment';

/**
 * The mark on a divergence line: its number and the spread there (e.g. "2 ·
 * Δ4.1"). Recharts injects `viewBox` when it clones this into the
 * <ReferenceLine>. Kept to two short tokens so six of them across a chart
 * stay readable at 10px.
 */
function DivergenceLabel(props: {
  viewBox?: { x?: number; y?: number };
  n: number;
  spread: number;
  color: string;
}) {
  const { viewBox, n, spread, color } = props;
  if (!viewBox || viewBox.x === undefined || viewBox.y === undefined) {
    return null;
  }
  return (
    <text
      x={viewBox.x + 3}
      y={viewBox.y + 11}
      fontSize={10}
      fontWeight={700}
      fill={color}
      textAnchor="start"
      style={{ pointerEvents: 'none' }}
    >
      {`${n} · Δ${spread.toFixed(1)}`}
    </text>
  );
}

/** One cross-participant divergence: when they pulled apart, by how much, and
 *  its number — the chart's mark and the list beneath it share it, so "amber
 *  line 3" and "the 3rd chip" are the same moment. */
interface Divergence {
  t: number;
  spread: number;
  n: number;
}
import { HandTrajectoryPanel } from './HandTrajectoryPanel';

/**
 * Multi-participant Focus Overlay (MULTI-PARTICIPANT-FOCUS).
 *
 * Overlays each team member's EEG-focus trace for one case on a single
 * absolute wall-clock axis, so a spike in the lead's focus and a dip in an
 * assistant's are read at the *same instant*. Gaps in a line are real
 * participation gaps (that person wasn't recording, or the Muse dropped).
 *
 * Phase 1: the overlay + hover compare + peak/valley markers + normalise.
 * The moment inspector (audio/video at a peak) is Phase 3 — it extends the
 * per-session machinery already in CognitiveTrace.
 */

// Categorical trace colours — distinct on both themes, kept off the
// semantic good/warn/bad hues used for EEG signal chips.
const COLORS = [
  '#fb7185', '#38bdf8', '#a78bfa', '#f5b642',
  '#34d399', '#f472b6', '#60a5fa', '#f87171',
];

const ROLE_LABELS: Record<string, string> = {
  lead_surgeon: 'Lead surgeon',
  fellow: 'Fellow',
  resident: 'Resident',
  circulator: 'Circulator',
  scrub_nurse: 'Scrub nurse',
  observer: 'Observer',
  other: 'Other',
};

interface PMeta {
  p: FocusParticipant;
  color: string;
  name: string;
  roleLabel: string | null;
  mean: number | null;
  offsetS: number;
  key: string;
}

/**
 * "Where was everyone at this instant" — each participant's value on the
 * active metric at the pinned moment, plus how far apart they were.
 *
 * These numbers previously existed only inside the chart's hover tooltip,
 * which you cannot hold open while watching the video: the two halves of
 * "what happened here?" could not be read at the same time. It fills the
 * space that used to be black bars beside a letterboxed clip.
 */
function MomentReadout({
  metas,
  chartData,
  pinnedSec,
  metric,
  divergence,
  fmt,
}: {
  metas: PMeta[];
  chartData: Record<string, number>[];
  pinnedSec: number | null;
  metric: string;
  divergence?: Divergence;
  fmt: (sec: number) => string;
}) {
  // The plotted row nearest the pin — the traces are binned, so the pinned
  // second and a row's `t` rarely match exactly.
  const row = useMemo(() => {
    if (pinnedSec == null || chartData.length === 0) return null;
    let best = chartData[0];
    let bestD = Infinity;
    for (const r of chartData) {
      const d = Math.abs(r.t - pinnedSec);
      if (d < bestD) {
        bestD = d;
        best = r;
      }
    }
    return best;
  }, [chartData, pinnedSec]);

  const values = metas
    .map((m) => ({ m, v: row?.[m.key] }))
    .filter((x): x is { m: PMeta; v: number } => typeof x.v === 'number');
  const spread =
    values.length >= 2
      ? Math.max(...values.map((x) => x.v)) - Math.min(...values.map((x) => x.v))
      : null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.default',
        px: 1,
        py: 0.75,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary' }}
        >
          AT THIS MOMENT
        </Typography>
        {pinnedSec != null && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {fmt(pinnedSec)}
          </Typography>
        )}
        {divergence && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`divergence ${divergence.n}`}
            sx={{ height: 18, fontSize: 11 }}
          />
        )}
      </Stack>

      {pinnedSec == null ? (
        <Typography variant="caption" color="text.secondary">
          Click the trace (or a transcript line) to inspect a moment.
        </Typography>
      ) : values.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No {metric} recorded here — a participation gap, or the sensor
          dropped.
        </Typography>
      ) : (
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {values.map(({ m, v }) => (
            <Stack
              key={m.key}
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  component="span"
                  sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: m.color }}
                />
                <Typography variant="caption">{m.name}</Typography>
              </Stack>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
              >
                {v.toFixed(1)}
              </Typography>
            </Stack>
          ))}
          {spread !== null && (
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ borderTop: 1, borderColor: 'divider', mt: 0.25, pt: 0.25 }}
            >
              <Typography variant="caption" color="text.secondary">
                spread
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                color={spread >= 2 ? 'warning.main' : 'text.secondary'}
              >
                {spread.toFixed(1)}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}

interface Props {
  tenantId: string;
  instanceId: string;
  /** Participant read-only view — fetch the overlay + all per-recording media
   *  (video, 3D) via the case-scoped /me endpoints. */
  asParticipant?: boolean;
}

export function FocusOverlay({ tenantId, instanceId, asParticipant = false }: Props) {
  const theme = useTheme();
  const fmtTime = useFormatTimeOfDay();
  const displayZone = useDisplayTimeZone();
  const [data, setData] = useState<FocusOverlayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<'focus' | 'engagement'>('focus');
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  // Absolute seconds-from-window-start pinned by clicking the chart.
  const [pinnedSec, setPinnedSec] = useState<number | null>(null);
  // One handle per participant's strip — a case can show several side by side,
  // and they play as one: the moment is the case's, not a single camera's.
  const mediaRefs = useRef<Map<string, MediaStripHandle | null>>(new Map());
  const registerMedia = useCallback(
    (key: string) => (h: MediaStripHandle | null) => {
      if (h) mediaRefs.current.set(key, h);
      else mediaRefs.current.delete(key);
    },
    [],
  );

  /**
   * Same gesture contract as the Dashboard's Cognitive trace: a click on
   * either chart, or a pick in the transcript, pins that moment and plays it;
   * clicking the moment you're already on stops the playback (and again
   * replays it). The pin stays where it is either way. With several strips on
   * screen the toggle drives all of them, so they never end up half-playing.
   */
  const pickMoment = useCallback(
    (t: number) => {
      if (isSameMoment(t, pinnedSec)) {
        for (const h of mediaRefs.current.values()) h?.togglePlayback();
        return;
      }
      setPinnedSec(t);
    },
    [pinnedSec],
  );
  // Reprocess (re-score) state for the empty-overlay recovery action.
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessMsg, setReprocessMsg] = useState<string | null>(null);

  const onReprocess = async () => {
    setReprocessing(true);
    setReprocessMsg(null);
    try {
      const r = await reprocessInstance(tenantId, instanceId);
      if (r.recordings_queued === 0) {
        setReprocessMsg(
          r.recordings_skipped > 0
            ? `Nothing to reprocess — ${r.recordings_skipped} attached recording(s) have no uploaded data.`
            : 'No recordings are attached to this case yet.',
        );
      } else {
        setReprocessMsg(
          `Queued ${r.recordings_queued} recording(s) for scoring` +
            (r.recordings_skipped
              ? ` (${r.recordings_skipped} skipped — no uploaded data)`
              : '') +
            '. Traces appear once scoring finishes — refresh in a minute.',
        );
      }
    } catch {
      setReprocessMsg('Could not queue reprocessing. Try again.');
    } finally {
      setReprocessing(false);
    }
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    setFocusedKey(null);
    setPinnedSec(null);
    (asParticipant ? getMyFocusOverlay(instanceId) : getFocusOverlay(tenantId, instanceId))
      .then((d) => live && setData(d))
      .catch(() => live && setError('Could not load focus data for this case.'))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [tenantId, instanceId, asParticipant]);

  // Poll while any recording's video is still preparing, so its progress bar
  // ticks and the clip appears once ready. Updates only `data` — never resets
  // the user's pinned moment / focused participant like the initial load does.
  const anyPreparing = (data?.participants ?? []).some((p) => p.video_prep);
  useEffect(() => {
    if (!anyPreparing) return;
    // `cancelled` guards against a stale in-flight response landing after the
    // user switched case/tab — without it a poll fetch for the PREVIOUS
    // instance can resolve late and overwrite the newly-selected recording.
    let cancelled = false;
    const id = setInterval(() => {
      (asParticipant
        ? getMyFocusOverlay(instanceId)
        : getFocusOverlay(tenantId, instanceId)
      )
        .then((d) => {
          if (!cancelled) setData(d);
        })
        .catch(() => undefined);
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [anyPreparing, tenantId, instanceId, asParticipant]);

  const windowStartMs = data?.window_start ? Date.parse(data.window_start) : null;
  const windowEndMs = data?.window_end ? Date.parse(data.window_end) : null;
  const totalS =
    windowStartMs != null && windowEndMs != null
      ? Math.max(1, Math.round((windowEndMs - windowStartMs) / 1000))
      : 1;

  const seriesOf = (p: FocusParticipant) => (metric === 'focus' ? p.focus : p.engagement);

  const metas: PMeta[] = useMemo(() => {
    if (!data || windowStartMs == null) return [];
    return data.participants
      .filter((p) => seriesOf(p).some((v) => v != null))
      .map((p, i) => {
        const s = seriesOf(p).filter((v): v is number => v != null);
        const mean = s.reduce((a, b) => a + b, 0) / s.length;
        return {
          p,
          color: COLORS[i % COLORS.length],
          name:
            p.participant_label ||
            (p.role ? ROLE_LABELS[p.role] ?? p.role : 'Participant'),
          roleLabel: p.role ? ROLE_LABELS[p.role] ?? p.role : null,
          mean,
          offsetS: Math.round((Date.parse(p.started_at) - windowStartMs) / 1000),
          key: p.session_row_id,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, windowStartMs, metric]);

  // Merge onto a shared second grid, binning long cases so recharts stays
  // smooth (~1500 points max). Sample i of a trace sits at offset + i*step_s.
  const chartData = useMemo(() => {
    const binS = Math.max(1, Math.ceil(totalS / 1500));
    const sums = new Map<number, Record<string, number>>();
    const cnts = new Map<number, Record<string, number>>();
    for (const m of metas) {
      const step = m.p.step_s || 1;
      seriesOf(m.p).forEach((v, i) => {
        // A gap stays a gap: the series is positional, so skip, never shift.
        if (v == null) return;
        const sec = Math.floor((m.offsetS + i * step) / binS) * binS;
        const val = v;
        const s = sums.get(sec) ?? {};
        const c = cnts.get(sec) ?? {};
        s[m.key] = (s[m.key] ?? 0) + val;
        c[m.key] = (c[m.key] ?? 0) + 1;
        sums.set(sec, s);
        cnts.set(sec, c);
      });
    }
    return [...sums.keys()]
      .sort((a, b) => a - b)
      .map((sec) => {
        const row: { t: number; [k: string]: number } = { t: sec };
        const s = sums.get(sec)!;
        const c = cnts.get(sec)!;
        for (const k of Object.keys(s)) row[k] = s[k] / c[k];
        return row;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metas, totalS, metric]);

  // Motion (|ω|) merged the same way, on the same wall-clock grid.
  const hasMotion = metas.some((m) => m.p.motion_trace.some((v) => v != null));
  const motionData = useMemo(() => {
    if (!hasMotion) return [];
    const binS = Math.max(1, Math.ceil(totalS / 1500));
    const sums = new Map<number, Record<string, number>>();
    const cnts = new Map<number, Record<string, number>>();
    for (const m of metas) {
      const step = m.p.motion_step_s || 1;
      m.p.motion_trace.forEach((v, i) => {
        if (v == null) return;
        const sec = Math.floor((m.offsetS + i * step) / binS) * binS;
        const s = sums.get(sec) ?? {};
        const c = cnts.get(sec) ?? {};
        s[m.key] = (s[m.key] ?? 0) + v;
        c[m.key] = (c[m.key] ?? 0) + 1;
        sums.set(sec, s);
        cnts.set(sec, c);
      });
    }
    return [...sums.keys()]
      .sort((a, b) => a - b)
      .map((sec) => {
        const row: { t: number; [k: string]: number } = { t: sec };
        const s = sums.get(sec)!;
        const c = cnts.get(sec)!;
        for (const k of Object.keys(s)) row[k] = s[k] / c[k];
        return row;
      });
  }, [metas, totalS, hasMotion]);

  // X-axis domain = the actual data extent (not the nominal case window), so
  // a recording that stopped producing data early doesn't squash the traces.
  // Shared across both charts to keep them wall-clock-aligned. See focusXDomain.
  const xDomain = useMemo<[number, number]>(
    () => focusXDomain(chartData, motionData, totalS),
    [chartData, motionData, totalS],
  );

  // One viewport for both charts (TRACE-VIEWPORT): they share a wall-clock
  // axis, so they must share a span and a scroll offset or the cognitive
  // trace and the hand motion below it stop lining up — which is the entire
  // point of stacking them.
  const viewport = useTraceViewport(xDomain[1] - xDomain[0]);
  const motionScrollRef = useRef<HTMLDivElement | null>(null);
  const syncScroll = (from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (from && to && to.scrollLeft !== from.scrollLeft) {
      to.scrollLeft = from.scrollLeft;
    }
  };

  // Cross-participant DIVERGENCE markers (MULTI-PARTICIPANT-FOCUS): vertical
  // lines where the people on this case pull apart most on the active metric —
  // the moments worth inspecting when two+ traces are overlaid. Computed
  // client-side off the already-aligned `chartData`: per time-bin spread
  // (max−min across participants present), local maxima above the 85th-pct
  // spread, greedily de-clustered (min gap) and capped so the chart stays
  // legible. Empty for a single participant (nothing to diverge from).
  const divergences = useMemo<Divergence[]>(() => {
    if (metas.length < 2 || chartData.length < 3) return [];
    const keys = metas.map((m) => m.key);
    const pts: { t: number; spread: number }[] = [];
    for (const row of chartData) {
      const vals: number[] = [];
      for (const k of keys) {
        const v = row[k];
        if (typeof v === 'number' && Number.isFinite(v)) vals.push(v);
      }
      if (vals.length >= 2) {
        pts.push({ t: row.t, spread: Math.max(...vals) - Math.min(...vals) });
      }
    }
    if (pts.length < 3) return [];
    const sorted = [...pts.map((p) => p.spread)].sort((a, b) => a - b);
    const thresh = sorted[Math.floor(0.85 * (sorted.length - 1))];
    if (!(thresh > 0)) return [];
    const cand = pts.filter((p, i) => {
      if (p.spread < thresh) return false;
      const prev = pts[i - 1]?.spread ?? -Infinity;
      const next = pts[i + 1]?.spread ?? -Infinity;
      return p.spread >= prev && p.spread >= next; // local maximum
    });
    cand.sort((a, b) => b.spread - a.spread);
    const minGap = Math.max(20, Math.round(totalS / 12));
    const picked: { t: number; spread: number }[] = [];
    for (const c of cand) {
      if (picked.every((p) => Math.abs(p.t - c.t) >= minGap)) picked.push(c);
      if (picked.length >= 6) break;
    }
    // Numbered in time order, so the chart's marks and the list under it read
    // as the same set. The spread rides along: a marker that claims a moment
    // matters should say how much they pulled apart there.
    return picked
      .sort((a, b) => a.t - b.t)
      .map((p, i) => ({ ...p, n: i + 1 }));
  }, [metas, chartData, totalS]);

  // Default the focused participant (drives the video panel) to the lead, else
  // the highest mean focus. Also RESET when the current key is stale — after a case
  // switch, `focusedKey` can still point at the previous case's recording, which
  // isn't in the new `metas`; without this it stays stale and `focused` goes
  // null (the video panel disappears).
  useEffect(() => {
    if (metas.length === 0) return;
    const valid = focusedKey != null && metas.some((m) => m.key === focusedKey);
    if (valid) return;
    const lead = metas.find((m) => m.p.role === 'lead_surgeon');
    const top = [...metas].sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0))[0];
    setFocusedKey((lead ?? top).key);
  }, [metas, focusedKey]);
  // Fall back to the first recording if the key is momentarily stale (the effect
  // above corrects it next tick), so the panel never blinks out on a switch.
  const focused =
    metas.find((m) => m.key === focusedKey) ?? metas[0] ?? null;

  // Seconds precision: these charts span minutes, so minute-precision ticks
  // printed the same label twice on a 3-minute case and a pinned moment could
  // not be told from one 50 s later. The zone is stated once (below) instead
  // of suffixed onto every tick.
  const tickTime = (sec: number) =>
    windowStartMs != null ? fmtTime(new Date(windowStartMs + sec * 1000)) : '';
  const zoneLabel =
    displayZone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone ??
    'local time';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (metas.length === 0) {
    return (
      <Stack spacing={1.5} sx={{ py: 1 }}>
        <Alert severity="info">
          No focus traces to plot yet. A recording appears here once its EEG
          (Muse) focus trace has been computed. If the recordings are attached
          and their individual scorecards look complete while this stays empty,
          the traces just need (re)processing — a recording scored before the
          trace existed won&apos;t have one.
        </Alert>
        {!asParticipant && (
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              disabled={reprocessing}
              onClick={() => void onReprocess()}
            >
              {reprocessing ? 'Queuing…' : 'Reprocess recordings'}
            </Button>
            {reprocessMsg && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                {reprocessMsg}
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    );
  }

  const eegChip = (p: FocusParticipant) => {
    if (p.eeg_hz == null && !p.eeg_status)
      return { label: 'no EEG', color: theme.palette.text.disabled };
    const ok = p.eeg_status === 'ok';
    return {
      label: p.eeg_hz != null ? `EEG ${Math.round(p.eeg_hz)} Hz` : `EEG ${p.eeg_status}`,
      color: ok ? theme.palette.success.main : theme.palette.warning.main,
    };
  };

  return (
    <Stack spacing={1.5}>
      {/* legend / roster — click to focus a participant (drives the video panel) */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {metas.map((m) => {
            const q = eegChip(m.p);
            const dim = focusedKey != null && focusedKey !== m.key;
            return (
              <MuiTooltip
                key={m.key}
                title={
                  `${m.roleLabel ?? 'Unassigned role'} · mean ${
                    m.mean?.toFixed(1) ?? '—'
                  } · covers ${Math.round(m.p.coverage_s / 60)} min` +
                  (m.p.coverage_s <
                  (Date.parse(m.p.ended_at) - Date.parse(m.p.started_at)) / 1000 - 30
                    ? ' (partial — EEG dropped)'
                    : '')
                }
              >
                <Chip
                  onClick={() => setFocusedKey(m.key)}
                  variant={focusedKey === m.key ? 'filled' : 'outlined'}
                  sx={{
                    opacity: dim ? 0.5 : 1,
                    borderColor: m.color,
                    '& .MuiChip-label': { display: 'flex', gap: 0.75, alignItems: 'center' },
                  }}
                  label={
                    <>
                      <Box
                        component="span"
                        sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: m.color }}
                      />
                      <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                        {m.name}
                      </Typography>
                      <Typography component="span" variant="caption" sx={{ color: q.color }}>
                        · {q.label}
                      </Typography>
                    </>
                  }
                />
              </MuiTooltip>
            );
          })}
        </Stack>
      </Stack>

      {/* The moment inspector: every participant's recording on the left, what
          was said and where everyone was on the right. Each strip seeks with
          its OWN offset, so a click on the trace shows the same wall-clock
          instant from each camera. Only the focused participant is audible —
          two soundtracks over each other is noise, and the other person's
          anchor audio would be actively misleading. */}
      {metas.length > 0 && (
        <MomentInspector
          // Several recordings share the media column, so it earns more of
          // the row than a single clip does.
          columns={metas.length > 1 ? '2fr 1fr' : '3fr 2fr'}
          media={
            <Stack
              // Side by side from tablet width up: two people on one case are
              // meant to be compared at a glance, and stacked they push the
              // second camera (and the traces) below the fold. Only a phone
              // is too narrow to carry both.
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems="stretch"
            >
              {metas.map((m) => (
                <Box key={m.key} sx={{ flex: 1, minWidth: 0 }}>
                  <MediaStrip
                    ref={registerMedia(m.key)}
                    tenantId={tenantId}
                    sessionRowId={m.p.session_row_id}
                    participantName={m.name}
                    startedAt={m.p.started_at}
                    selectedTime={
                      pinnedSec == null
                        ? null
                        : Math.max(0, pinnedSec - m.offsetS)
                    }
                    asParticipant={asParticipant}
                    instanceId={instanceId}
                    videoPrep={m.p.video_prep}
                    muted={focusedKey != null && m.key !== focusedKey}
                  />
                </Box>
              ))}
            </Stack>
          }
          aside={
            <>
              <MomentReadout
                metas={metas}
                chartData={chartData}
                pinnedSec={pinnedSec}
                metric={metric}
                divergence={divergences.find((d) => isSameMoment(pinnedSec, d.t))}
                fmt={tickTime}
              />
              {focused && (focused.p.transcript?.length ?? 0) > 0 && (
                <CaptionTrack
                  entries={(focused.p.transcript ?? []).map(
                    (c): CaptionEntry => ({
                      t: focused.offsetS + c.time_s,
                      headline: c.headline,
                      transcript: c.transcript,
                      summary: c.summary,
                    }),
                  )}
                  pinnedT={pinnedSec}
                  onPick={pickMoment}
                  // Wall clock, like every other time on this page — the rows
                  // used to read 0:22 while the chart they drive read 11:00 AM.
                  formatStamp={tickTime}
                  // Beside the video: take whatever height the row has.
                  fill
                />
              )}
            </>
          }
        />
      )}

      {/* Which signal the overlay plots. It lives here, on the chart it
          drives, rather than up in the case header: on a laptop the header
          scrolls off behind the video and the roster, and switching Focus ↔
          Engagement meant scrolling back up to a control you couldn't see
          from the chart. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary' }}
        >
          COGNITIVE (EEG)
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={metric}
          onChange={(_, v) => v && setMetric(v)}
        >
          <ToggleButton value="focus">Focus</ToggleButton>
          <ToggleButton value="engagement">Engagement</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <TraceZoom
        spanS={viewport.spanS}
        onChange={viewport.setSpanS}
        durationS={xDomain[1] - xDomain[0]}
      />
      <Box
        ref={viewport.ref}
        onScroll={(e) => syncScroll(e.currentTarget, motionScrollRef.current)}
        sx={{
          width: '100%',
          height: 340,
          overflowX: viewport.scrolls ? 'auto' : 'hidden',
          overflowY: 'hidden',
        }}
        data-testid="focus-overlay-chart"
      >
        <ResponsiveContainer
          width={viewport.chartWidth || '100%'}
          height="100%"
        >
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
            onClick={(s: { activeLabel?: string | number }) => {
              const v = s?.activeLabel;
              if (typeof v === 'number') pickMoment(v);
            }}
          >
            <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="t"
              domain={xDomain}
              tickFormatter={tickTime}
              stroke={theme.palette.text.secondary}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              domain={[0, 'auto']}
              stroke={theme.palette.text.secondary}
              tick={{ fontSize: 11 }}
              width={40}
              label={{
                value: metric,
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: 11, fill: theme.palette.text.secondary },
              }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const rows = payload.filter((r) => r.value != null);
                if (!rows.length) return null;
                const vals = rows.map((r) => r.value as number);
                const spread = Math.max(...vals) - Math.min(...vals);
                return (
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      fontSize: 12,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {tickTime(label as number)}
                    </Typography>
                    {rows
                      .slice()
                      .sort((a, b) => (b.value as number) - (a.value as number))
                      .map((r) => {
                        const m = metas.find((x) => x.key === r.dataKey);
                        return (
                          <Box
                            key={r.dataKey as string}
                            sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}
                          >
                            <span style={{ color: m?.color }}>{m?.name}</span>
                            <b>{Math.round(r.value as number)}</b>
                          </Box>
                        );
                      })}
                    {rows.length > 1 && (
                      <Box sx={{ mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
                        spread{' '}
                        <b style={{ color: theme.palette.error.main }}>
                          {Math.round(spread)}
                        </b>
                      </Box>
                    )}
                  </Box>
                );
              }}
            />
            {/* The pinned moment — set by a click here, on the motion chart
                below, or by picking a transcript line. Same weight + stamped
                label as the Dashboard's Cognitive trace so the one marker
                these charts draw reads the same everywhere; stamped in this
                chart's units (shared wall clock, not session-elapsed). */}
            {pinnedSec != null && (
              <ReferenceLine
                x={pinnedSec}
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                label={
                  <PinnedMomentLabel
                    text={tickTime(pinnedSec)}
                    color={theme.palette.primary.main}
                    halo={theme.palette.background.paper}
                  />
                }
              />
            )}
            {/* Cross-participant divergence markers (2+ participants). Each
                carries its number and how far apart they were, because an
                unlabelled amber line reads as chart furniture rather than a
                finding — only the first used to say anything at all. The
                numbered chips under the chart pin these same moments. */}
            {divergences.map((d) => (
              <ReferenceLine
                key={`div-${d.n}`}
                x={d.t}
                stroke={theme.palette.warning.main}
                strokeDasharray="4 3"
                strokeOpacity={0.75}
                label={
                  <DivergenceLabel
                    n={d.n}
                    spread={d.spread}
                    color={theme.palette.warning.main}
                  />
                }
              />
            ))}
            {metas.map((m) => (
              <Line
                key={m.key}
                type="monotone"
                dataKey={m.key}
                name={m.name}
                stroke={m.color}
                strokeWidth={m.p.role === 'lead_surgeon' ? 2.6 : 1.8}
                strokeOpacity={focusedKey && focusedKey !== m.key ? 0.55 : 1}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Hand motion (|ω|) trace — same axis, same pinned moment. */}
      {hasMotion && (
        <>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary', mt: 0.5 }}
          >
            HAND MOTION (IMU) · angular speed °/s
          </Typography>
          <Box
            ref={motionScrollRef}
            onScroll={(e) => syncScroll(e.currentTarget, viewport.el.current)}
            sx={{
              width: '100%',
              height: 180,
              overflowX: viewport.scrolls ? 'auto' : 'hidden',
              overflowY: 'hidden',
            }}
            data-testid="motion-overlay-chart"
          >
            <ResponsiveContainer
              width={viewport.chartWidth || '100%'}
              height="100%"
            >
              <LineChart
                data={motionData}
                margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
                onClick={(s: { activeLabel?: string | number }) => {
                  const v = s?.activeLabel;
                  if (typeof v === 'number') pickMoment(v);
                }}
              >
                <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="t"
                  domain={xDomain}
                  tickFormatter={tickTime}
                  stroke={theme.palette.text.secondary}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  domain={[0, 'auto']}
                  stroke={theme.palette.text.secondary}
                  tick={{ fontSize: 11 }}
                  width={40}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const rows = payload.filter((r) => r.value != null);
                    if (!rows.length) return null;
                    return (
                      <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, p: 1, fontSize: 12 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {tickTime(label as number)}
                        </Typography>
                        {rows.map((r) => {
                          const m = metas.find((x) => x.key === r.dataKey);
                          return (
                            <Box key={r.dataKey as string} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                              <span style={{ color: m?.color }}>{m?.name}</span>
                              <b>{Math.round(r.value as number)}</b>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }}
                />
                {pinnedSec != null && (
                  <ReferenceLine
                    x={pinnedSec}
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    label={
                      <PinnedMomentLabel
                        text={tickTime(pinnedSec)}
                        color={theme.palette.primary.main}
                        halo={theme.palette.background.paper}
                      />
                    }
                  />
                )}
                {metas.map((m) => (
                  <Line
                    key={m.key}
                    type="monotone"
                    dataKey={m.key}
                    name={m.name}
                    stroke={m.color}
                    strokeWidth={1.6}
                    strokeOpacity={focusedKey && focusedKey !== m.key ? 0.55 : 1}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}

      {/* 3D hand path for the focused participant, revealed to the pinned moment. */}
      {focused && (
        <HandTrajectoryPanel
          tenantId={tenantId}
          sessionRowId={focused.p.session_row_id}
          participantName={focused.name}
          tSec={pinnedSec == null ? null : Math.max(0, pinnedSec - focused.offsetS)}
          asParticipant={asParticipant}
          instanceId={instanceId}
        />
      )}

      {/* The divergence moments as a list you can act on. The amber lines say
          WHERE the team pulled apart; these say when, by how much, and take
          you there — chart click, video seek and transcript scroll in one
          gesture. Numbered to match the marks above. */}
      {divergences.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary' }}
          >
            DIVERGED AT
          </Typography>
          {divergences.map((d) => (
            <Chip
              key={`div-chip-${d.n}`}
              size="small"
              variant={isSameMoment(pinnedSec, d.t) ? 'filled' : 'outlined'}
              color="warning"
              onClick={() => pickMoment(d.t)}
              label={`${d.n} · ${tickTime(d.t)} · Δ${d.spread.toFixed(1)}`}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            />
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary">
        Overlaid on absolute time · {tickTime(xDomain[0])}–{tickTime(xDomain[1])}{' '}
        {zoneLabel}. Click a point on either trace to pin a moment — the video
        above jumps there and plays, and the transcript scrolls to what was
        being said. Line gaps are participation gaps (not recording, or sensor
        dropped).{' '}
        {divergences.length > 0
          ? 'Amber marks flag where participants diverge most on this metric; Δ is how far apart they were.'
          : metas.length < 2
            ? 'Divergence markers appear once two or more participants share a case.'
            : ''}{' '}
        Hand motion is drift-robust angular speed (|ω|), not a spatial path.
      </Typography>
    </Stack>
  );
}
